import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnDestroy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TranslateModule } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

export interface FileUploadData {
  file: File | null;
  previewUrl: string; // URL or Object URL for preview
  fileName: string;
  fileSize: number;
  isExisting?: boolean; // Flag to indicate if this is an existing image from server
  removed?: boolean; // Flag to indicate if this image was removed in edit mode
  onFileChanged?: boolean; // Flag to indicate if this file was changed and should be submitted
  isVideo?: boolean; // Flag to render a <video> preview instead of an <img>
}

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    TranslateModule,
  ],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
})
export class FileUploadComponent implements ControlValueAccessor, OnDestroy {
  @Input() placeholder: string =
    'common.form.placeholders.click_to_upload_drag_drop';
  @Input() accept: string = '';
  @Input() multiple: boolean = false;
  @Input() maxSize: number = 5 * 1024 * 1024; // 5MB default
  @Input() acceptedTypes: string = '';
  @Input() disabled: boolean = false;
  @Input() editMode: boolean = false; // New input to indicate edit mode
  // Optional client-side pre-validation for video files (fast feedback before
  // upload; the server remains authoritative - see VideoValidationService).
  @Input() videoMinDurationSec?: number;
  @Input() videoMaxDurationSec?: number;
  @Input() videoMaxWidthPx?: number;
  @Input() videoMaxHeightPx?: number;

  @Output() fileChange = new EventEmitter<FileUploadData | null>();
  @Output() imageRemoved = new EventEmitter<string>(); // New output for removed image names
  @Output() multipleFilesSelected = new EventEmitter<FileUploadData[]>(); // New output for multiple files

  uploadedFile: FileUploadData | null = null;
  isDragOver = false;
  errorMessage = '';
  errorParams: any = {};
  isValidatingVideo = false;

  private onChange = (value: any) => {};
  private onTouched = () => {};
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(private readonly messageService: MessageService) {}

  // ControlValueAccessor implementation
  writeValue(value: FileUploadData | null): void {
    if (value) {
      // If we have previewUrl but no file, create a dummy file for display
      if (value.previewUrl && !value.file) {
        const dummyFile = new File(
          [],
          value.fileName || (value.isVideo ? 'video.mp4' : 'image.jpg'),
          { type: value.isVideo ? 'video/mp4' : 'image/jpeg' },
        );
        value.file = dummyFile;
        value.isExisting = true; // Mark as existing image from server
      }

      // Ensure existing images don't have onFileChanged flag set
      if (value.isExisting) {
        value.onFileChanged = false;
      }

      // Clear error messages when a valid file is set
      this.errorMessage = '';
      this.errorParams = {};
    } else {
      // Clear error messages when no file is set
      this.errorMessage = '';
      this.errorParams = {};
    }

    this.uploadedFile = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // File handling methods
  onFileSelected(event: any): void {
    if (this.disabled) return;

    // Clear previous error messages when new files are selected
    this.errorMessage = '';
    this.errorParams = {};

    const files = event.target.files;
    if (files && files.length > 0) {
      if (this.multiple && files.length > 1) {
        // Handle multiple files
        this.handleMultipleFiles(files);
      } else {
        // Handle single file
        this.handleFile(files[0]);
      }
    }
  }

  onDragOver(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    if (this.disabled) return;

    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      if (this.multiple && files.length > 1) {
        // Handle multiple files
        this.handleMultipleFiles(files);
      } else {
        // Handle single file
        this.handleFile(files[0]);
      }
    }
  }

  private async handleFile(file: File): Promise<void> {
    // Clear previous error messages
    this.errorMessage = '';
    this.errorParams = {};

    // Validate file type
    if (this.accept && !this.isFileTypeValid(file)) {
      this.errorMessage = `common.validation.invalid_file_type`;
      return;
    }

    // Validate file size
    if (file.size > this.maxSize) {
      const maxSizeFormatted = this.formatFileSize(this.maxSize);
      this.errorMessage = `common.validation.file_too_large_with_limit`;
      this.errorParams = { maxSize: maxSizeFormatted };
      return;
    }

    try {
      const isVideo = this.isVideoFile(file);
      // Create Object URL for preview only for image/video files
      const objectUrl =
        this.isImageFile(file) || isVideo ? URL.createObjectURL(file) : '';

      // Fast client-side pre-check for duration/resolution (server remains
      // authoritative - see VideoValidationService/ffprobe on goods-service)
      if (isVideo && this.hasVideoConstraints()) {
        this.isValidatingVideo = true;

        // Runs before validateVideoMetadata, which can't catch a renamed fake
        // (wrong-codec <video> may never fire onloadedmetadata).
        if (file.name.toLowerCase().endsWith('.mp4')) {
          const isRealMp4 = await this.hasMp4MagicBytes(file);
          if (!isRealMp4) {
            this.isValidatingVideo = false;
            URL.revokeObjectURL(objectUrl);
            this.errorMessage = 'common.validation.video_invalid_mp4_signature';
            this.errorParams = {};
            this.cdr.markForCheck();
            return;
          }
        }

        const metadataError = await this.validateVideoMetadata(objectUrl);
        this.isValidatingVideo = false;
        if (metadataError) {
          URL.revokeObjectURL(objectUrl);
          this.errorMessage = metadataError.key;
          this.errorParams = metadataError.params ?? {};
          this.cdr.markForCheck();
          return;
        }
        this.cdr.markForCheck();
      }

      // In edit mode, selecting a new file replaces the existing server image.
      // Emit removal so parent DTO can populate removeMainImages/removeDetailImages.
      if (this.editMode && this.uploadedFile?.isExisting) {
        const replacedFile = this.uploadedFile;
        this.imageRemoved.emit(replacedFile.fileName);

        if (
          replacedFile.previewUrl &&
          replacedFile.file &&
          this.isPreviewableMediaFile(replacedFile.file)
        ) {
          URL.revokeObjectURL(replacedFile.previewUrl);
        }
      }

      const fileData: FileUploadData = {
        file,
        previewUrl: objectUrl,
        fileName: file.name,
        fileSize: file.size,
        isExisting: false, // New file
        removed: false,
        onFileChanged: true, // Mark as changed for submission
        isVideo,
      };

      this.uploadedFile = fileData;
      this.onChange(fileData);
      this.onTouched();
      this.fileChange.emit(fileData);

      // Clear any error messages when file is successfully processed
      this.errorMessage = '';
      this.errorParams = {};
    } catch {
      this.errorMessage = 'common.validation.file_processing_error';
    }
  }

  private async handleMultipleFiles(files: FileList): Promise<void> {
    this.errorMessage = '';
    this.errorParams = {};

    const processedFiles: FileUploadData[] = [];
    const errors: string[] = [];

    for (const file of Array.from(files)) {
      try {
        // Validate file type
        if (this.accept && !this.isFileTypeValid(file)) {
          errors.push(`${file.name}: Invalid file type`);
          continue;
        }

        // Validate file size
        if (file.size > this.maxSize) {
          const maxSizeFormatted = this.formatFileSize(this.maxSize);
          errors.push(`${file.name}: File too large (max ${maxSizeFormatted})`);
          continue;
        }

        // Create Object URL for preview only for image files
        const objectUrl = this.isImageFile(file)
          ? URL.createObjectURL(file)
          : '';

        const fileData: FileUploadData = {
          file,
          previewUrl: objectUrl,
          fileName: file.name,
          fileSize: file.size,
          isExisting: false,
          removed: false,
          onFileChanged: true,
        };

        processedFiles.push(fileData);
      } catch {
        errors.push(`${file.name}: Processing error`);
      }
    }

    // Show errors if any
    if (errors.length > 0) {
      this.errorMessage = errors.join(', ');
    } else {
      // Clear error messages if no errors
      this.errorMessage = '';
      this.errorParams = {};
    }

    // Emit all processed files
    if (processedFiles.length > 0) {
      this.multipleFilesSelected.emit(processedFiles);
    }

    // Clear the input
    const input = document.querySelector(
      'input[type="file"]',
    ) as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  // New method to remove existing image in edit mode
  removeExistingImage(): void {
    console.log('removeExistingImage called:', this.uploadedFile);
    if (this.uploadedFile?.isExisting) {
      // Store the filename before clearing the object
      const fileName = this.uploadedFile.fileName;
      const previewUrl = this.uploadedFile.previewUrl;

      console.log('Emitting imageRemoved event with fileName:', fileName);
      // Emit the filename for backend to know which image to remove
      this.imageRemoved.emit(fileName);

      // Revoke Object URL to prevent memory leaks (only for image files)
      if (
        previewUrl &&
        this.uploadedFile?.file &&
        this.isPreviewableMediaFile(this.uploadedFile.file)
      ) {
        URL.revokeObjectURL(previewUrl);
      }

      // Clear the file data
      this.uploadedFile = null;
      this.onChange(null);
      this.onTouched();
      this.fileChange.emit(null);
    }
  }

  removeFile(): void {
    console.log('removeFile called:', {
      disabled: this.disabled,
      uploadedFile: this.uploadedFile,
      editMode: this.editMode,
    });
    if (this.disabled) return;

    if (this.uploadedFile) {
      // Emit the removal event for tracking (both existing and new files)
      if (this.editMode && this.uploadedFile.isExisting) {
        // For existing images, emit the filename for backend tracking
        console.log(
          'Emitting imageRemoved event for existing image:',
          this.uploadedFile.fileName,
        );
        this.imageRemoved.emit(this.uploadedFile.fileName);
      } else if (this.editMode && !this.uploadedFile.isExisting) {
        // For newly uploaded files, also emit for tracking
        console.log(
          'Emitting imageRemoved event for new image:',
          this.uploadedFile.fileName,
        );
        this.imageRemoved.emit(this.uploadedFile.fileName);
      }

      // Revoke Object URL to prevent memory leaks (only for image files)
      if (
        this.uploadedFile.previewUrl &&
        this.uploadedFile.file &&
        this.isPreviewableMediaFile(this.uploadedFile.file)
      ) {
        URL.revokeObjectURL(this.uploadedFile.previewUrl);
      }

      this.uploadedFile = null;
      this.onChange(null);
      this.onTouched();
      this.fileChange.emit(null);

      // Clear error messages when file is removed
      this.errorMessage = '';
      this.errorParams = {};
    }
  }

  // Utility methods
  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  isVideoFile(file: File): boolean {
    return file.type.startsWith('video/');
  }

  private isPreviewableMediaFile(file: File): boolean {
    return this.isImageFile(file) || this.isVideoFile(file);
  }

  private hasVideoConstraints(): boolean {
    return (
      this.videoMinDurationSec !== undefined ||
      this.videoMaxDurationSec !== undefined ||
      this.videoMaxWidthPx !== undefined ||
      this.videoMaxHeightPx !== undefined
    );
  }

  // MP4 files carry the ASCII box type "ftyp" at byte offset 4.
  private async hasMp4MagicBytes(file: File): Promise<boolean> {
    const header = new Uint8Array(await file.slice(0, 8).arrayBuffer());
    if (header.length < 8) {
      return false;
    }
    const ftyp = [0x66, 0x74, 0x79, 0x70]; // "ftyp"
    return ftyp.every((byte, i) => header[4 + i] === byte);
  }

  // Reads duration/resolution off a hidden <video> element. Resolves to null
  // (no error) if metadata can't be read - the server validates authoritatively.
  private validateVideoMetadata(
    objectUrl: string,
  ): Promise<{ key: string; params?: any } | null> {
    return new Promise((resolve) => {
      const videoEl = document.createElement('video');
      videoEl.preload = 'metadata';
      // Some codecs never fire loadedmetadata/error in-browser; don't hang the
      // upload UI forever waiting - fall back to server-side validation.
      const timeoutId = setTimeout(() => resolve(null), 8000);
      videoEl.onloadedmetadata = () => {
        clearTimeout(timeoutId);
        const { duration, videoWidth, videoHeight } = videoEl;
        if (
          this.videoMinDurationSec !== undefined &&
          duration < this.videoMinDurationSec
        ) {
          resolve({
            key: 'common.validation.video_too_short',
            params: { minDuration: this.videoMinDurationSec },
          });
        } else if (
          this.videoMaxDurationSec !== undefined &&
          duration > this.videoMaxDurationSec
        ) {
          resolve({
            key: 'common.validation.video_too_long',
            params: { maxDuration: this.videoMaxDurationSec },
          });
        } else if (
          (this.videoMaxWidthPx !== undefined &&
            videoWidth > this.videoMaxWidthPx) ||
          (this.videoMaxHeightPx !== undefined &&
            videoHeight > this.videoMaxHeightPx)
        ) {
          resolve({
            key: 'common.validation.video_resolution_too_large',
            params: {
              maxWidth: this.videoMaxWidthPx,
              maxHeight: this.videoMaxHeightPx,
            },
          });
        } else {
          resolve(null);
        }
      };
      videoEl.onerror = () => {
        clearTimeout(timeoutId);
        resolve(null);
      };
      videoEl.src = objectUrl;
    });
  }

  isExcelFile(fileName: string): boolean {
    if (!fileName) return false;
    const extension = fileName.split('.').pop()?.toLowerCase();
    return extension === 'xlsx' || extension === 'xls';
  }

  isFileTypeValid(file: File): boolean {
    if (!this.accept) return true;

    const acceptedTypes = this.accept.split(',').map((type) => type.trim());
    return acceptedTypes.some((type) => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      return file.type === type || file.type.startsWith(type.replace('*', ''));
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return (
      Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    );
  }

  // Image event handlers
  onImageError(_event: Event): void {}

  onImageLoad(_event: Event): void {}

  getFileSizeDisplay(): string {
    return this.uploadedFile?.fileSize
      ? this.formatFileSize(this.uploadedFile.fileSize)
      : 'Unknown';
  }

  // Method to programmatically reset the component
  reset(): void {
    if (this.uploadedFile) {
      // Revoke Object URL to prevent memory leaks (only for image files)
      if (
        this.uploadedFile.previewUrl &&
        this.uploadedFile.file &&
        this.isPreviewableMediaFile(this.uploadedFile.file)
      ) {
        URL.revokeObjectURL(this.uploadedFile.previewUrl);
      }
    }

    // Clear all state
    this.uploadedFile = null;
    this.errorMessage = '';
    this.errorParams = {};
    this.isDragOver = false;

    // Clear the file input
    const input = document.querySelector(
      'input[type="file"]',
    ) as HTMLInputElement;
    if (input) {
      input.value = '';
    }

    // Emit null to notify parent components
    this.onChange(null);
    this.onTouched();
    this.fileChange.emit(null);
  }

  // Cleanup Object URLs on component destruction
  ngOnDestroy(): void {
    if (
      this.uploadedFile?.previewUrl &&
      this.uploadedFile.file &&
      this.isPreviewableMediaFile(this.uploadedFile.file)
    ) {
      URL.revokeObjectURL(this.uploadedFile.previewUrl);
    }
  }
}
